/**
 * NLP Data Extractor
 * Extracts structured data from natural language using LLM
 */

import { HOST } from '@api/constant';

interface SchemaField {
	type: string;
	label?: string;
	placeholder?: string;
	validation?: {
		required?: boolean;
		min?: number;
		max?: number;
		pattern?: string;
	};
}

interface ExtractionResult {
	extractedData: Record<string, any>;
	confidence: number;
	missingFields: string[];
}

/**
 * Create a prompt for the LLM to extract data from user input
 */
const createExtractionPrompt = (
	userPrompt: string,
	schema: Record<string, SchemaField>,
	actionDescription: string
): string => {
	const schemaDescription = Object.entries(schema)
		.map(([fieldName, field]) => {
			const required = field.validation?.required
				? '(REQUIRED)'
				: '(optional)';
			const type = field.type || 'string';
			return `  - ${fieldName} ${required}: ${field.label || fieldName} (${type})`;
		})
		.join('\n');

	return `You are a data extraction assistant. Extract structured data from the user's natural language request.

User wants to: ${actionDescription}

User said: "${userPrompt}"

Schema fields needed:
${schemaDescription}

INSTRUCTIONS:
1. Extract any relevant data from the user's prompt that matches the schema fields
2. Use best-effort matching - infer reasonable values when possible
3. Leave fields empty if not mentioned or cannot be inferred
4. For dates, convert natural language (e.g., "next week", "tomorrow") to ISO format
5. For booleans, interpret yes/no language
6. Return ONLY valid JSON in this exact format:

{
  "formVals": {
    "fieldName": "extracted value",
    "anotherField": "another value"
  },
  "confidence": 0.85,
  "missingRequired": ["field1", "field2"]
}

IMPORTANT: 
- Only include fields where you found data
- confidence should be 0-1 (how sure you are about the extraction)
- missingRequired should list required fields that weren't found
- Return valid JSON only, no markdown, no explanation`;
};

/**
 * Extract data from user prompt using LLM
 */
export const extractDataFromPrompt = async (
	userPrompt: string,
	schema: Record<string, SchemaField>,
	actionDescription: string
): Promise<ExtractionResult> => {
	try {
		const extractionPrompt = createExtractionPrompt(
			userPrompt,
			schema,
			actionDescription
		);

		// Call the AI help endpoint with the extraction prompt (non-streaming for data extraction)
		const response = await fetch(`${HOST}/api/ai/help`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			credentials: 'include', // Include cookies for authentication
			body: JSON.stringify({
				query: extractionPrompt,
				conversation_history: [],
				context: 'Data extraction for form pre-filling',
				max_results: 1,
				stream: false, // Disable streaming for data extraction
			}),
		});

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		// Parse the JSON response
		const responseData = await response.json();
		let responseText = '';
		if (typeof responseData === 'string') {
			responseText = responseData;
		} else if (responseData?.response) {
			responseText = responseData.response;
		} else if (responseData?.data?.response) {
			responseText = responseData.data.response;
		}

		// Try to extract JSON from the response
		const jsonMatch = responseText.match(/\{[\s\S]*"formVals"[\s\S]*\}/);
		if (jsonMatch) {
			try {
				const parsed = JSON.parse(jsonMatch[0]);
				return {
					extractedData: parsed.formVals || {},
					confidence: parsed.confidence || 0.5,
					missingFields: parsed.missingRequired || [],
				};
			} catch (parseError) {
				console.warn('⚠️ [extractDataFromPrompt] Failed to parse JSON with formVals:', parseError);
				// Continue to fallback
			}
		}

		// Fallback: Try to find any JSON object
		const anyJsonMatch = responseText.match(/\{[\s\S]*\}/);
		if (anyJsonMatch) {
			try {
				const parsed = JSON.parse(anyJsonMatch[0]);
				if (parsed.formVals) {
					return {
						extractedData: parsed.formVals,
						confidence: parsed.confidence || 0.5,
						missingFields: parsed.missingRequired || [],
					};
				}
			} catch (parseError) {
				console.warn('⚠️ [extractDataFromPrompt] Failed to parse JSON object:', parseError);
				// Continue to return empty result
			}
		}

		// No valid JSON found
		return {
			extractedData: {},
			confidence: 0,
			missingFields: Object.keys(schema).filter(
				(key) => schema[key].validation?.required
			),
		};
	} catch (error) {
		console.error('❌ Failed to extract data from prompt:', error);
		return {
			extractedData: {},
			confidence: 0,
			missingFields: Object.keys(schema).filter(
				(key) => schema[key].validation?.required
			),
		};
	}
};

/**
 * Simple rule-based extraction for common patterns
 * Used as a fast fallback before calling LLM
 */
export const quickExtractData = (
	userPrompt: string,
	schema: Record<string, SchemaField>
): Partial<Record<string, any>> => {
	const extracted: Record<string, any> = {};
	const lowerPrompt = userPrompt.toLowerCase();

	// Common patterns
	Object.entries(schema).forEach(([fieldName, field]) => {
		const lowerFieldName = fieldName.toLowerCase();

		// Extract based on field type
		switch (field.type) {
			case 'string':
			case 'text':
				// Look for "name: value" or "title: value" patterns
				const stringPatterns = [
					new RegExp(`${lowerFieldName}[:\\s]+([^,\\.]+)`, 'i'),
					new RegExp(`called\\s+([^,\\.]+)`, 'i'),
					new RegExp(`named\\s+([^,\\.]+)`, 'i'),
				];
				for (const pattern of stringPatterns) {
					const match = userPrompt.match(pattern);
					if (match && match[1]) {
						extracted[fieldName] = match[1].trim();
						break;
					}
				}
				break;

			case 'date':
				// Look for date-related words
				const datePatterns = [
					{
						pattern: /tomorrow/i,
						value: () => {
							const d = new Date();
							d.setDate(d.getDate() + 1);
							return d.toISOString();
						},
					},
					{
						pattern: /next week/i,
						value: () => {
							const d = new Date();
							d.setDate(d.getDate() + 7);
							return d.toISOString();
						},
					},
					{
						pattern: /next month/i,
						value: () => {
							const d = new Date();
							d.setMonth(d.getMonth() + 1);
							return d.toISOString();
						},
					},
					{
						pattern: /(\d{4}-\d{2}-\d{2})/i,
						value: (match: RegExpMatchArray) => {
							return new Date(match[1]).toISOString();
						},
					},
				];

				for (const { pattern, value } of datePatterns) {
					const match = userPrompt.match(pattern);
					if (match) {
						extracted[fieldName] =
							typeof value === 'function' ? value(match) : value;
						break;
					}
				}
				break;

			case 'number':
				// Extract numbers near the field name
				const numberPattern = new RegExp(
					`${lowerFieldName}[:\\s]+(\\d+)`,
					'i'
				);
				const numberMatch = userPrompt.match(numberPattern);
				if (numberMatch) {
					extracted[fieldName] = parseInt(numberMatch[1], 10);
				}
				break;

			case 'boolean':
				// Look for yes/no language
				const yesPatterns = ['yes', 'true', 'enable', 'on', 'active'];
				const noPatterns = [
					'no',
					'false',
					'disable',
					'off',
					'inactive',
				];

				if (yesPatterns.some((p) => lowerPrompt.includes(p))) {
					extracted[fieldName] = true;
				} else if (noPatterns.some((p) => lowerPrompt.includes(p))) {
					extracted[fieldName] = false;
				}
				break;
		}
	});

	return extracted;
};

/**
 * Hybrid extraction: Quick rules first, then LLM for missing data
 */
export const extractDataHybrid = async (
	userPrompt: string,
	schema: Record<string, SchemaField>,
	actionDescription: string
): Promise<ExtractionResult> => {
	// First, try quick rule-based extraction
	const quickExtracted = quickExtractData(userPrompt, schema);

	// Find missing required fields
	const requiredFields = Object.entries(schema)
		.filter(([key, field]) => field.validation?.required)
		.map(([key]) => key);

	const missingRequired = requiredFields.filter(
		(field) => !quickExtracted[field]
	);

	// If we got all required fields, return immediately
	if (missingRequired.length === 0) {
		return {
			extractedData: quickExtracted,
			confidence: 0.7,
			missingFields: [],
		};
	}

	// Otherwise, use LLM for more sophisticated extraction
	const llmResult = await extractDataFromPrompt(
		userPrompt,
		schema,
		actionDescription
	);

	// Merge quick extraction with LLM extraction (LLM takes precedence)
	const mergedData = {
		...quickExtracted,
		...llmResult.extractedData,
	};

	// Recalculate missing fields
	const stillMissing = requiredFields.filter((field) => !mergedData[field]);

	return {
		extractedData: mergedData,
		confidence: llmResult.confidence,
		missingFields: stillMissing,
	};
};
